import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { User } from '../api';
import { telephonyCallsRedirectTarget } from '../telephony/TelephonySection';
import {
  PORTAL_ROUTES,
  canAccessRoute,
  type PortalRedirectId,
  type PortalRouteDefinition,
  type PortalScreenId,
} from './navigation';

export type PortalScreens = Readonly<Record<PortalScreenId, ReactNode>>;

export interface PortalRoutesProps {
  user: Pick<User, 'userType' | 'role' | 'roles'>;
  screens: PortalScreens;
}

export function portalRedirectTarget(kind: PortalRedirectId, search: string, id = '') {
  if (kind === 'telephony-calls') return telephonyCallsRedirectTarget(search);
  if (kind === 'contact-center-mail' || kind === 'contact-center-mail-compose') {
    const params = new URLSearchParams(search);
    const legacyMessageId = params.get('message');
    if (legacyMessageId) {
      params.delete('message');
      params.set('mailMessage', legacyMessageId);
    }
    params.set('channel', 'EMAIL');
    if (kind === 'contact-center-mail-compose') params.set('compose', '1');
    return `/contact-center?${params.toString()}`;
  }
  if (kind === 'tickets-kanban') return '/tickets?view=kanban';
  if (kind === 'tickets-new') {
    const params = new URLSearchParams(search);
    params.set('create', '1');
    return `/tickets?${params.toString()}`;
  }
  if (kind === 'templates-new') return '/templates?create=1';
  return `/templates?edit=${encodeURIComponent(id)}`;
}

function PortalRouteElement({
  definition,
  user,
  screens,
}: PortalRoutesProps & { definition: PortalRouteDefinition }) {
  const location = useLocation();
  const { id = '' } = useParams<{ id: string }>();

  if (!canAccessRoute(definition, user)) return <Navigate to="/" replace />;
  if (definition.redirect) {
    return <Navigate to={portalRedirectTarget(definition.redirect, location.search, id)} replace />;
  }
  if (!definition.screen) return <Navigate to="/" replace />;
  return screens[definition.screen];
}

export function PortalRoutes({ user, screens }: PortalRoutesProps) {
  return (
    <Routes>
      {PORTAL_ROUTES.map(definition => (
        <Route
          key={definition.path}
          path={definition.path}
          element={<PortalRouteElement definition={definition} user={user} screens={screens} />}
        />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
